<template>
  <VDialogV3
    id="demand-order-create-dialog"
    position="bottom"
    show-edit-button
    :loading="isLoading"
    :title="`Создать потребности на ${isPreorder ? 'предзаказ' : 'заказ'}`"
    :submit-disabled="submitDisabled"
    :errors="dialogErrors"
    @edit-button:clicked="openBuyerRequisitesDialog"
    @submit="onSave"
  >
    <header class="document-header">
      <dl>
        <dt>Покупатель</dt>
        <dd>{{ baseDemand?.buyer?.name ?? '--' }}</dd>
        <dt>Юр. лицо</dt>
        <dd>{{ baseDemand?.buyerCompany?.name ?? '--' }}</dd>
      </dl>

      <dl>
        <dt>Склад</dt>
        <dd>{{ baseDemand?.buyerStore?.name ?? '--' }}</dd>
        <dt>Ответственный</dt>
        <dd>{{ baseDemand?.responsibleBuyer?.name ?? '--' }}</dd>
      </dl>
    </header>

    <VDataGridV2
      :data="products"
      layout="fill"
    >
      <VDataGridColumnV2
        id="index"
        label="#"
        width="40px"
        content-align="center"
        content-class="nowrap"
      >
        <template #default="{ row, index }">
          {{ index + 1 }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="code"
        label="Арт товара"
        width="100px"
      >
        <template #default="{ row }">
          <VInput
            v-model="row.product.code"
            required
            :max-length="300"
            :validate-method="value => validateCode(value)"
            @input="setProductInDemandDebounced($event, row)"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="qty"
        label="Кол-во"
        width="120px"
        content-align="right"
      >
        <template #default="{ row }">
          <VInputNumberRusV2
            v-model="row.qtyDesired"
            :type="Qty"
            required
            text-align="right"
            :unit="row.product.unit?.name ?? ''"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        v-if="isPreorder"
        id="preorderMonthlyReserve"
        label="Страх. запас, кол-во"
        width="150px"
        content-align="right"
      >
        <template #default="{ row }">
          <VInputNumberRusV2
            v-model="row.preorderMonthlyReserve"
            :type="Qty"
            required
            text-align="right"
            :validate-method="value => validatePreorderMonthlyReserve(value, row)"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        v-if="isPreorder"
        id="preorderShipmentPeriod"
        width="150px"
        content-align="center"
      >
        <template #label>
          Период поставки
          <VLink @click="showMassChangeDialog('preorderShipmentPeriod')">
            Установить общий период
          </VLink>
        </template>
        <template #default="{ row }">
          <VDateRangeInputNew
            v-model="row.preorderShipmentPeriod"
            required
            :validate-method="validateDate"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        v-else
        id="desiredReceiptDate"
        width="150px"
      >
        <template #label>
          Дата поставки
          <VLink @click="showMassChangeDialog('desiredReceiptDate')">
            Установить общую дату
          </VLink>
        </template>
        <template #default="{ row }">
          <VDateInput
            v-model="row.desiredReceiptDate"
            required
            :validate-method="validateDate"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="buyerProductionSite"
        width="150px"
      >
        <template #label>
          Площадка
          <VLink @click="showMassChangeDialog('buyerProductionSite')">
            Установить общую площадку
          </VLink>
        </template>
        <template #default="{ row }">
          <VSelectV2
            v-model="row.buyerProductionSite"
            :data="row.buyerCompany.productionSites"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="responsibleBuyer"
        width="150px"
      >
        <template #label>
          Ответственный
          <VLink @click="showMassChangeDialog('responsibleBuyer')">
            Установить общего отв-ного
          </VLink>
        </template>
        <template #default="{ row }">
          <VSelectV2
            v-model="row.responsibleBuyer"
            :data="({pattern}) => PersonsService.searchPersons(pattern, $core.currentPartner.id)"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="product-info"
        label="Товар"
        width="240px"
      >
        <template #default="{ row }">
          <template v-if="row.product.category">
            <div :title="'Категория - ' + (row.product.category.name || 'Не найдено')">
              Категория -
              <b :class="{ error: !row.product.id || !row.product.category }">
                {{ row.product.category.name || 'Не найдено' }}
              </b>
            </div>
            <div :title="'Название товара - ' + row.product.name">
              Название товара -
              <b :class="{ error: !row.product.id }">{{ row.product.name }}</b>
            </div>
          </template>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="action"
        width="52px"
      >
        <template #default="{ row }">
          <VButton
            title="Удалить"
            @click="deleteRow(row)"
          >
            <template #icon>
              <VIcon material="delete"/>
            </template>
          </VButton>
        </template>
      </VDataGridColumnV2>

      <VDataGridActionBarV2 class="centered">
        <VLink material="add" @click="addRow">
          Добавить строчку
        </VLink>
        <VLink material="add" @click="onStartImport">
          Импортировать
        </VLink>
      </VDataGridActionBarV2>
    </VDataGridV2>
  </VDialogV3>
</template>

<script lang="ts">

import DateSimple      from '@/core/types/DateSimple';
import Demand          from '@/domains/Demand/Demand';
import Product         from '@/domains/Products/Product';
import ProductsService from '@/domains/Products/ProductsService';
import DemandsService  from '@/domains/Demand/DemandsService';
import PersonsService  from '@/domains/Persons/PersonsService';
import Qty from "@/core/types/Qty";
import debounce from "lodash.debounce";

export default defineComponent({
  name: 'DemandOrderCreateDialog',

  props: {
    isPreorder: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['close', 'save'],

  setup() {
    return {
      PersonsService,
      Qty
    };
  },

  data() {
    return {
      /** позиции это товары из которых мы сделаем потребности */
      products: [] as Demand[],
      isLoading: true,
      isSaving: false
    };
  },

  computed: {
    baseDemand(): Demand | null {
      return this.products[0] ?? null;
    },

    /** Есть строка без распознанного товара (нет product.id) */
    isProductMissing(): boolean {
      return this.products.some(demand => !demand?.product?.id);
    },

    submitDisabled(): boolean {
      return this.isProductMissing || this.isSaving;
    },

    /** Сводные ошибки для плашки над футером VDialogV3 */
    dialogErrors(): { message: string }[] {
      if (this.isProductMissing) {
        return [{ message: 'Некоторые товары не найдены или без категории' }];
      }
      else {
        return [];
      }
    },
  },

  async created() {
    const sampleDemand            = Demand.fromNew();
    sampleDemand.product          = new Product();
    sampleDemand.isPreorderNeeded = this.isPreorder;
    await sampleDemand.buyerCompany.loadProductionSites();

    this.products.push(sampleDemand);
    this.isLoading = false;
  },

  methods: {
    /**
     * Открывает диалог редактирования данных покупателя.
     * Применяет изменения сразу ко всем позициям формы.
     */

    openBuyerRequisitesDialog(): void {
      this.$core.dialogs.DemandBuyerRequisitesDialog.show(
        { baseDemand: this.baseDemand },
        {
          save: (updatedDemand: Demand) => {
            this.products.forEach(demand => {
              demand.buyer               = updatedDemand.buyer;
              demand.buyerCompany        = updatedDemand.buyerCompany;
              demand.buyerStore          = updatedDemand.buyerStore;
              demand.responsibleBuyer    = updatedDemand.responsibleBuyer;
              demand.buyerProductionSite = updatedDemand.buyerProductionSite;
            });
          },
        },
      );
    },

    /** Проставляет значение поля во все позиции формы */
    setTheSameField(fieldName: string, value: any): void {
      this.products.forEach(demand => {
        (demand as any)[fieldName] = value;
      });
    },

    /** Открывает диалог массовой замены поля (берёт значение первой позиции) */
    showMassChangeDialog(fieldName: string): void {
      this.$core.dialogs.MassChangeDialog.show(
        {
          fieldName,
          presetValue:           this.products[0]?.[fieldName] ?? null,
          productionSiteOptions: this.products[0]?.buyerCompany?.productionSites,
        },
        {
          save: (result: Record<string, any>) => {
            this.setTheSameField(fieldName, result[fieldName]);
          },
        },
      );
    },

    /** Сохраняет потребности. Вызывается из @submit (VDialogV3 уже провалидировал форму). */
    async onSave(): Promise<void> {
      this.isSaving = true;

      const success = await DemandsService.add(this.products);

      this.isSaving = false;

      if (success) {
        this.$emit('save');
        this.$core.dialogs.DemandOrderCreateDialog.hide();
      }
    },

    /** Добавляет новую строку, наследуя данные покупателя от первой */
    addRow(): void {
      const newDemand   = this.$core.utils.cloneDeep(Demand.fromAnotherDemand(this.products[0]));
      newDemand.product = new Product();

      this.products = [...this.products, newDemand];
    },

    /** Удаляет строку по индексу (последнюю строку удалить нельзя) */
    deleteRow(demand: Demand): void {
      if (this.products.length === 1) return;
      this.products = this.products.filter(item => item !== demand);
    },

    setProductInDemandDebounced: debounce(async function(productCode: string, demand: Demand) {
      await this.setProductInDemand(productCode, demand);
    }, 400),

    /** Загружает товар по артикулу и проставляет его в позицию */
    async setProductInDemand(productCode: string, demand: Demand): Promise<void> {
      const products = await DemandsService.findProductsByCodes([productCode]);

      if (this.$core.utils.isNotEmptyArray(products) === false) return;

      demand.product = await ProductsService.get(products[0].productid);
    },

    /** Проверяет, что артикул соответствует существующему товару с категорией */
    async validateCode(value: string): Promise<string | null> {
      if (value === null || value.length === 0) {
        return null;
      }

      const products = await DemandsService.findProductsByCodes([value]);

      if (this.$core.utils.isNotEmptyArray(products) === false) {
        return 'Не найден товар для этого артикула';
      }

      if (!products[0].productname) {
        return 'У данного товара не найдена категория';
      }

      return null;
    },

    /** Страховой запас не может превышать желаемое количество позиции */
    validatePreorderMonthlyReserve(value: Qty, row: Demand): string | null {
      if (value?.isPositive() && row.qtyDesired?.isPositive() && value?.greaterThan(row.qtyDesired)
      ) {
        return 'Не может быть больше общего количества';
      }
      else {
        return null;
      }
    },

    /** Запрещает прошедшую дату поставки */
    validateDate(date: any): string | null {
      if (this.$core.utils.date.isDate(date) === false) return null;

      const today      = DateSimple.today();
      const dateSimple = new DateSimple(date);

      if (dateSimple.isBefore(today) || dateSimple.equals(today)) {
        return 'Нельзя указать прошедшую дату';
      }

      return null;
    },

    /** Открывает диалог импорта позиций; импортированные строки добавляются в форму */
    onStartImport(): void {
      this.$core.dialogs.DemandImportDialog.show(
        { isPreorder: this.isPreorder },
        {
          onImport: async (items: any[]) => {
            const demands: Demand[] = [];

            for (const item of items) {
              const newDemand   = Demand.fromAnotherDemand(this.products.at(-1)!);
              newDemand.product = new Product();
              await this.setProductInDemand(item.buyersCode, newDemand);

              newDemand.qtyDesired = item.qty;

              if (this.isPreorder) {
                newDemand.preorderMonthlyReserve = item.preorderMonthlyReserve;
              }

              demands.push(newDemand);
            }

            // Пустые строки убираем, оставляем заполненные + импортированные
            const products = this.products.filter(item => item?.product?.code);
            products.push(...demands);

            this.products = products;
          },
        },
      );
    },
  },
});
</script>
